import React, { useCallback, useEffect, useState } from "react";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { SharedAccess } from "target_wasm";
import { ManageAccessDialog } from "components/shareSection/ManageAccessDialog";
import { Callout } from "components/common/Callout";
import { InlineComposerButton } from "components/common/InlineComposerButton";
import { SelectCheckbox } from "components/common/SelectCheckbox";
import {
  RemoveAccessData,
  ResourceType,
} from "components/shareSection/entities";
import { useSharedAccessManager } from "components/shareSection/useSharedAccessManager";
import {
  fetchDatasetSharedWithList,
  listDatasetReleases,
  releaseDataset,
  revokeDatasetShare,
  shareDataset,
} from "@/lib/api/datasets";
import {
  RELEASE_COMPOSER_HELPER,
  releaseErrorMessage,
} from "@/components/main/datasets/releaseCopy";

interface ShareDatasetProps {
  datasetId: string;
  onClose: () => void;
}

/// A dataset's share dialog: everyone on it is a viewer, sharing waits for a
/// release, and a toggle co-grants the owner's shareable member tables.
export const ShareDataset: React.FC<ShareDatasetProps> = ({
  datasetId,
  onClose,
}) => {
  const [sharedWith, setSharedWith] = useState<SharedAccess | undefined>(
    undefined,
  );
  const [shareOwnedTables, setShareOwnedTables] = useState(true);
  // Release gate: "loading" until we know, then "has" (share freely) or "none"
  // (publish inline first).
  const [releaseStatus, setReleaseStatus] = useState<
    "loading" | "has" | "none"
  >("loading");

  const manager = useSharedAccessManager({
    entityId: datasetId,
    setSharedWithList: setSharedWith,
    fetchFn: fetchDatasetSharedWithList,
  });

  useEffect(() => {
    let cancelled = false;
    listDatasetReleases(datasetId)
      .then((r) => {
        if (!cancelled)
          setReleaseStatus(r.releases.length > 0 ? "has" : "none");
      })
      // Unknown ⇒ enforce (show the composer); the server is the real gate.
      .catch(() => {
        if (!cancelled) setReleaseStatus("none");
      });
    return () => {
      cancelled = true;
    };
  }, [datasetId]);

  // Publish the inline release, then lift the gate. Throwing surfaces the
  // message inline in the composer popover.
  const publishRelease = useCallback(
    async (name: string) => {
      try {
        await releaseDataset(datasetId, name);
      } catch (e) {
        throw new Error(releaseErrorMessage(e));
      }
      setReleaseStatus("has");
    },
    [datasetId],
  );

  const handleUserShare = useCallback(
    async (emails: string[]) => {
      const { atCapacityEmails, invalidEmails } = await shareDataset(
        datasetId,
        { emails, shareOwnedTables },
      );
      return { atCapacityEmails, refusedEmails: invalidEmails };
    },
    [datasetId, shareOwnedTables],
  );

  const handleRevokeAccess = useCallback(
    async (data: RemoveAccessData | null) => {
      if (!data) return;
      await revokeDatasetShare(datasetId, { userEmail: data.email });
      setSharedWith((prev) => prev?.remove_user_by_email(data.email ?? ""));
    },
    [datasetId],
  );

  const needsRelease = releaseStatus === "none";

  return (
    <ManageAccessDialog
      open={true}
      onClose={onClose}
      entityId={datasetId}
      resourceType={ResourceType.Dataset}
      sharedWithList={sharedWith}
      setSharedWithList={setSharedWith}
      canShare={true}
      shareDisabled={releaseStatus !== "has"}
      canRevokeAccess={true}
      onShare={handleUserShare}
      onRevokeAccess={handleRevokeAccess}
      onSuccess={manager.handleSuccess}
      onError={manager.handleError}
      onRefetchSharedList={manager.handleRefetchSharedList}
      userContinuationToken={manager.userContinuationToken}
      loadFailed={manager.loadFailed}
      onRetryLoad={manager.retryLoad}
      notice={
        <Callout variant="info">
          <div className="flex flex-col gap-2">
            <span>
              People you share with only see this dataset&apos;s released
              tables, each at its released version.
            </span>
            {needsRelease && (
              <div>
                <InlineComposerButton
                  label="Add a release"
                  icon={<RocketLaunchOutlinedIcon className="icon-xs" />}
                  placeholder="Release name — e.g. 2026-q3"
                  actionLabel="Release"
                  helper={RELEASE_COMPOSER_HELPER}
                  onSubmit={publishRelease}
                />
              </div>
            )}
          </div>
        </Callout>
      }
      shareOptions={
        <label className="flex cursor-pointer items-start gap-2 text-fg-muted-regular select-none">
          {/* One line tall, so the box centres on the text's first line. */}
          <span className="flex h-[1lh] shrink-0 items-center">
            <SelectCheckbox
              selected={shareOwnedTables}
              onToggle={() => setShareOwnedTables((v) => !v)}
              ariaLabel="Also give access to the tables I own in this dataset"
            />
          </span>
          <span>
            Also give access to the tables I own here, so they can open the
            data. Tables I can&apos;t share stay locked.
          </span>
        </label>
      }
    />
  );
};
